import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Stack,
    Switch,
    Typography
} from '@mui/material';
import { PatternFormat } from 'react-number-format';
import capitalize from 'utils/capitalize';
import { userRoles, userRolesNames } from '../../utils/constants';
import { Loader } from '../index';
import { hasModules } from '../../utils/org-modules';
import { moduleKeys } from '../../utils/permissions';

// Roles that start from the same clean slate as a fresh tenant/occupant invite:
// no restrictions unless the manager checks them.
const unrestrictedByDefaultRoles = [userRoles.TENANT, userRoles.RESIDENT];

const normalizeRole = role =>
    String(role || '')
        .trim()
        .toLowerCase();

const roleLabel = role => {
    if (!role) return 'Guest';

    const key = String(role).trim().toLowerCase();

    const map = {
        resi: 'Occupant',
        tenant: userRolesNames.tenant,
        guest: userRolesNames.guest
    };

    if (map[key]) return map[key];

    const s = String(role).replace(/_/g, ' ').trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
};

const ConvertUserModal = ({
    onClose,
    member,
    propertyId,
    fromRole,
    toRole,
    loading = false,
    onConfirm,
    activeOrg
}) => {
    const [primary, setPrimary] = useState(false);
    const [minor, setMinor] = useState(false);
    const [hideFromCalls, setHideFromCalls] = useState(false);
    const [disableGuestInvites, setDisableGuestInvites] = useState(false);

    const guestsEnabled = hasModules(activeOrg, moduleKeys.GUESTS);

    const [submitting, setSubmitting] = useState(false);
    const isLoading = loading || submitting;

    const initialValues = useMemo(() => {
        const prop = propertyId ? member?.properties?.[propertyId] : null;

        const primaryVal = !!prop?.primary;
        const minorVal = !!prop?.minor;

        const callsAllowed = prop?.permissions?.calls;
        const inviteAllowed = prop?.permissions?.invite;

        // Converting to tenant/occupant starts with the same defaults as inviting a
        // fresh one - both restrictions off. Seeding them from the guest record left
        // converted users hidden from the call directory and unable to invite guests
        // without the manager ever setting that.
        const startsUnrestricted = unrestrictedByDefaultRoles.includes(
            normalizeRole(toRole)
        );

        let hideFromCallsVal = callsAllowed === undefined ? false : !callsAllowed;

        let disableGuestInvitesVal = inviteAllowed === undefined ? false : !inviteAllowed;

        if (startsUnrestricted) {
            hideFromCallsVal = false;
            disableGuestInvitesVal = false;
        }

        // A minor always implies both restrictions, same rule as the invite modals.
        if (minorVal) {
            hideFromCallsVal = true;
            disableGuestInvitesVal = true;
        }

        return {
            primary: primaryVal,
            minor: minorVal,
            hideFromCalls: hideFromCallsVal,
            disableGuestInvites: disableGuestInvitesVal
        };
    }, [member, propertyId, toRole]);

    useEffect(() => {
        setPrimary(initialValues.primary);
        setMinor(initialValues.minor);
        setHideFromCalls(initialValues.hideFromCalls);
        setDisableGuestInvites(initialValues.disableGuestInvites);
        setSubmitting(false);
    }, [member?.uid, member?.id, propertyId, initialValues]);

    const fullName = useMemo(() => {
        const fn = capitalize(member?.first_name || '');
        const ln = capitalize(member?.last_name || '');
        return `${fn} ${ln}`.trim() || '-';
    }, [member]);

    const phone = useMemo(() => {
        const code = member?.phone?.code ? String(member.phone.code) : '';
        const number = member?.phone?.number ? String(member.phone.number) : '';
        return { code, number };
    }, [member]);

    const handlePrimary = checked => {
        setPrimary(checked);
        if (checked) setMinor(false);
    };

    const handleMinor = checked => {
        setMinor(checked);
        if (checked) {
            setPrimary(false);
            setHideFromCalls(true);
            setDisableGuestInvites(true);
        }
    };

    const canEditRestrictions = !minor;

    const submit = async () => {
        if (isLoading) return;

        setSubmitting(true);
        try {
            await onConfirm?.({
                primary,
                minor,
                hideFromCalls,
                disableGuestInvites
            });

            onClose?.();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {isLoading && <Loader />}
            <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
                    {`Make ${roleLabel(toRole)}`}
                </Typography>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ px: 3, py: 2.5 }}>
                <Stack spacing={2.25}>
                    <Box>
                        <Grid container spacing={1.25}>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    sx={{ fontSize: 13, color: 'text.secondary' }}
                                >
                                    Name
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
                                    {fullName}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography
                                    sx={{ fontSize: 13, color: 'text.secondary' }}
                                >
                                    Email
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
                                    {member?.email || '-'}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography
                                    sx={{ fontSize: 13, color: 'text.secondary' }}
                                >
                                    Phone
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
                                    {phone.number ? (
                                        <PatternFormat
                                            value={phone.number}
                                            displayType={'text'}
                                            format={
                                                phone.code
                                                    ? `+${phone.code} (###)-###-####`
                                                    : '(###)-###-####'
                                            }
                                        />
                                    ) : (
                                        '-'
                                    )}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography
                                    sx={{ fontSize: 13, color: 'text.secondary' }}
                                >
                                    Change
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
                                    {roleLabel(fromRole)} → {roleLabel(toRole)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                            Options
                        </Typography>

                        <Grid container spacing={1.25}>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    sx={{
                                        m: 0,
                                        width: '100%',
                                        justifyContent: 'space-between'
                                    }}
                                    label={
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    color: 'text.primary'
                                                }}
                                            >
                                                Owner
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 12.5,
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                Mark user as primary for this property
                                            </Typography>
                                        </Box>
                                    }
                                    labelPlacement="start"
                                    control={
                                        <Switch
                                            checked={primary}
                                            onChange={(_, checked) =>
                                                handlePrimary(checked)
                                            }
                                            disabled={isLoading || minor}
                                        />
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    sx={{
                                        m: 0,
                                        width: '100%',
                                        justifyContent: 'space-between'
                                    }}
                                    label={
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    color: 'text.primary'
                                                }}
                                            >
                                                Minor
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 12.5,
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                Auto-enables restrictions
                                            </Typography>
                                        </Box>
                                    }
                                    labelPlacement="start"
                                    control={
                                        <Switch
                                            checked={minor}
                                            onChange={(_, checked) =>
                                                handleMinor(checked)
                                            }
                                            disabled={isLoading || primary}
                                        />
                                    }
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    sx={{ m: 0 }}
                                    control={
                                        <Checkbox
                                            checked={hideFromCalls}
                                            onChange={() => setHideFromCalls(v => !v)}
                                            disabled={isLoading || !canEditRestrictions}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{ fontSize: 14, color: 'text.primary' }}
                                        >
                                            Hide from calls
                                        </Typography>
                                    }
                                />
                                <Typography
                                    sx={{
                                        fontSize: 12.5,
                                        color: 'text.secondary',
                                        ml: 4.25,
                                        mt: -0.25
                                    }}
                                >
                                    User will not appear in call directory for this
                                    property
                                </Typography>
                            </Grid>

                            {guestsEnabled && (
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        sx={{ m: 0 }}
                                        control={
                                            <Checkbox
                                                checked={disableGuestInvites}
                                                onChange={() =>
                                                    setDisableGuestInvites(v => !v)
                                                }
                                                disabled={
                                                    isLoading || !canEditRestrictions
                                                }
                                            />
                                        }
                                        label={
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    color: 'text.primary'
                                                }}
                                            >
                                                Disable guest invites
                                            </Typography>
                                        }
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: 12.5,
                                            color: 'text.secondary',
                                            ml: 4.25,
                                            mt: -0.25
                                        }}
                                    >
                                        Prevent user from sending guest invitations
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Stack>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="error" disabled={isLoading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={submit}
                    disabled={isLoading}
                    sx={{ textTransform: 'none' }}
                >
                    {isLoading ? 'Converting...' : 'Convert'}
                </Button>
            </DialogActions>
        </>
    );
};

export default ConvertUserModal;
